import { z } from "zod";

import { guardNever } from "../errors.js";

/**
 * A zod validation issue reduced to fields that are safe to log.
 *
 * Everything here is derived from the schema (expected types, enum options,
 * size bounds) or from zod's parsed-type names — never from the raw input
 * value. In particular this intentionally drops:
 * - `message`, which interpolates the received value for some issue codes
 *   (e.g. `invalid_enum_value`) and may contain arbitrary input via custom
 *   error maps or `.refine(..., { message })`
 * - `received` on `invalid_literal` / `invalid_enum_value` issues
 * - `keys` on `unrecognized_keys` issues
 * - `params` on `custom` issues
 *
 * Caveat: `path` segments come from the schema for object schemas, but for
 * `z.record(...)` (or any schema validating user-controlled keys) the failing
 * key itself appears in the path. Don't use these helpers to log validation
 * failures of schemas whose keys are sensitive user input.
 */
export interface SafeLoggableZodIssue {
  code: z.ZodIssueCode;
  /** Dot-joined issue path, or "(root)" for a top-level failure. */
  path: string;
  /** Human-readable summary built only from schema-side issue fields. */
  detail?: string;
}

function issuePathToString(path: (string | number)[]): string {
  return path.length === 0 ? "(root)" : path.join(".");
}

function safeIssueDetail(issue: z.ZodIssue): string | undefined {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      return `expected ${issue.expected}, received ${issue.received}`;
    case z.ZodIssueCode.invalid_literal:
      return `expected literal ${String(issue.expected)}`;
    case z.ZodIssueCode.unrecognized_keys:
      return `${issue.keys.length} unrecognized key(s)`;
    case z.ZodIssueCode.invalid_union_discriminator:
    case z.ZodIssueCode.invalid_enum_value:
      return `expected one of ${issue.options.map(String).join(" | ")}`;
    case z.ZodIssueCode.invalid_string:
      return typeof issue.validation === "string"
        ? `failed ${issue.validation} validation`
        : `failed ${Object.keys(issue.validation).join("/")} validation`;
    case z.ZodIssueCode.too_small:
      return `${issue.type} below minimum ${issue.minimum}`;
    case z.ZodIssueCode.too_big:
      return `${issue.type} above maximum ${issue.maximum}`;
    case z.ZodIssueCode.not_multiple_of:
      return `not a multiple of ${issue.multipleOf}`;
    case z.ZodIssueCode.not_finite:
      return "not finite";
    case z.ZodIssueCode.invalid_union:
    case z.ZodIssueCode.invalid_arguments:
    case z.ZodIssueCode.invalid_return_type:
    case z.ZodIssueCode.invalid_date:
    case z.ZodIssueCode.invalid_intersection_types:
    case z.ZodIssueCode.custom:
      return undefined;
    default:
      guardNever(issue, undefined);
      return undefined;
  }
}

function collectSafeIssues(
  error: z.ZodError,
  seen: Set<string>,
  out: SafeLoggableZodIssue[],
): void {
  for (const issue of error.issues) {
    const nestedErrors =
      issue.code === z.ZodIssueCode.invalid_union
        ? issue.unionErrors
        : issue.code === z.ZodIssueCode.invalid_arguments
          ? [issue.argumentsError]
          : issue.code === z.ZodIssueCode.invalid_return_type
            ? [issue.returnTypeError]
            : undefined;
    if (nestedErrors != null) {
      for (const nested of nestedErrors) {
        collectSafeIssues(nested, seen, out);
      }
      continue;
    }

    const safeIssue: SafeLoggableZodIssue = {
      code: issue.code,
      path: issuePathToString(issue.path),
      detail: safeIssueDetail(issue),
    };
    const key = `${safeIssue.code}|${safeIssue.path}|${safeIssue.detail ?? ""}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(safeIssue);
    }
  }
}

/**
 * Extracts issues from a {@link z.ZodError} in a form that is safe to log
 * without leaking the validated input. Union (and function argument/return)
 * errors are flattened into their nested issues, and exact duplicates that
 * different union branches produce are deduplicated.
 *
 * See {@link SafeLoggableZodIssue} for exactly what is and isn't included.
 */
export function getSafeLoggableZodIssues(
  error: z.ZodError,
): SafeLoggableZodIssue[] {
  const out: SafeLoggableZodIssue[] = [];
  collectSafeIssues(error, new Set(), out);
  return out;
}

/**
 * Formats a {@link z.ZodError} as a single input-free string suitable for log
 * messages, e.g.
 * `invalid_type at config.apiKey (expected string, received number); invalid_enum_value at mode (expected one of a | b)`.
 *
 * Prefer {@link getSafeLoggableZodIssues} when logging structured metadata.
 */
export function formatZodErrorForLogging(error: z.ZodError): string {
  return getSafeLoggableZodIssues(error)
    .map(({ code, detail, path }) =>
      detail != null ? `${code} at ${path} (${detail})` : `${code} at ${path}`,
    )
    .join("; ");
}
